import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import {
  CircularProgress,
  Grid,
  Typography,
  styled,
  tableCellClasses,
} from '@mui/material';
import EjectIcon from '@mui/icons-material/Eject';
import { blueColor, nameDiv, pointerCursor, rotateIcon } from '../../styles';

export default function UMTable(props) {
  const {
    rows,
    orderHandler,
    OrderSortBy,
    showTable,
    openEditUserModel = () => {},
  } = props;
  const { t } = useTranslation();

  const columns = [
    { id: 'UserId', label: t('ID') },
    { id: 'UserName', label: t('Name') },
    { id: 'Email', label: t('Email') },
    { id: 'SiteName', label: t('Site') },
    { id: 'DepartmentName', label: t('Department') },
    { id: 'Active', label: t('Active') },
  ];

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const [columnOrderData, setColumnOrderData] = useState({
    columnId: 'UserId',
    order: 'desc',
  });

  useEffect(() => {
    if (
      OrderSortBy &&
      OrderSortBy.SortBy !== '' &&
      OrderSortBy.OrderBy !== ''
    ) {
      setColumnOrderData({
        columnId: OrderSortBy.SortBy,
        order: OrderSortBy.OrderBy,
      });
    }
  }, [OrderSortBy]);

  //To handle page change
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  //To handle the change of rows per page
  const handleChangeRowsPerPage = event => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  //to set the page index as 0 whenever rows(list) is changing
  useEffect(() => {
    setPage(0);
  }, [rows]);

  //To handle the Sorting
  const handleOrderChange = (columnId, order) => {
    setColumnOrderData({
      columnId: columnId,
      order: order,
    });
    orderHandler(columnId, order);
  };

  const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
      backgroundColor: '#1F305E',
      color: theme.palette.common.white,
      fontWeight: 'bold',
      fontSize: '17px',
    },
    [`&.${tableCellClasses.body}`]: {
      fontSize: '17px',
    },
  }));

  const StyledTableRow = styled(TableRow)(({ theme }) => ({
    '&:nth-of-type(even)': {
      backgroundColor: theme.palette.action.hover,
    },
  }));

  return showTable ? (
    <Paper sx={{ width: '100%', overflow: 'hidden' }}>
      <TableContainer sx={{ maxHeight: 600 }}>
        <Table stickyHeader aria-label="sticky table">
          <TableHead>
            <TableRow>
              {columns.map(column => (
                <StyledTableCell key={column.id} align="center">
                  <Grid style={nameDiv}>
                    <Grid>{column.label}</Grid>
                    <Grid>
                      {column.id === columnOrderData.columnId ? (
                        columnOrderData.order === 'desc' ? (
                          <EjectIcon
                            style={pointerCursor}
                            onClick={() => handleOrderChange(column.id, 'asc')}
                          />
                        ) : (
                          <EjectIcon
                            style={rotateIcon}
                            onClick={() => handleOrderChange(column.id, 'desc')}
                          />
                        )
                      ) : (
                        <Grid
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                          }}
                        >
                          <EjectIcon
                            style={pointerCursor}
                            onClick={() => handleOrderChange(column.id, 'desc')}
                            fontSize="30px"
                          />
                          <EjectIcon
                            style={rotateIcon}
                            onClick={() => handleOrderChange(column.id, 'asc')}
                            fontSize="30px"
                          />
                        </Grid>
                      )}
                    </Grid>
                  </Grid>
                </StyledTableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((row, index) => {
                return (
                  <StyledTableRow
                    hover
                    role="checkbox"
                    tabIndex={-1}
                    key={index}
                  >
                    <StyledTableCell align="center">
                      <Typography width={80} sx={{ cursor: 'default' }}>
                        {row.UserId}
                      </Typography>
                    </StyledTableCell>
                    <StyledTableCell align="center">
                      <Grid
                        style={blueColor}
                        onClick={() => openEditUserModel(row.UserId)}
                        sx={{
                          cursor: 'pointer',
                        }}
                      >
                        <Typography noWrap width={310}>
                          {row.UserName}
                        </Typography>
                      </Grid>
                    </StyledTableCell>
                    <StyledTableCell align="center">
                      <Typography
                        key={index}
                        width={375}
                        sx={{ cursor: 'default' }}
                      >
                        {row.Email}
                      </Typography>
                    </StyledTableCell>
                    <StyledTableCell align="center">
                      <Typography
                        key={index}
                        width={120}
                        sx={{ cursor: 'default' }}
                      >
                        {row.SiteName}
                      </Typography>
                    </StyledTableCell>
                    <StyledTableCell align="center">
                      <Typography width={230} sx={{ cursor: 'default' }}>
                        {row.DepartmentName}
                      </Typography>
                    </StyledTableCell>
                    <StyledTableCell align="center">
                      <Typography width={130} sx={{ cursor: 'default' }}>
                        {row.Active === '1' ? t('Yes') : t('No')}
                      </Typography>
                    </StyledTableCell>
                  </StyledTableRow>
                );
              })}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[10, 25, 100]}
        component="div"
        count={rows.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        labelRowsPerPage={`${t('Rows_per_page')}:`}
        sx={{ boxShadow: 'inset 0 0 2px #000000' }}
      />
    </Paper>
  ) : (
    <CircularProgress />
  );
}
